import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PanelLeftClose,
  LogOut,
  ChevronDown,
  Search,
  Sparkles,
  HelpCircle,
  Settings,
  Users,
  Volume2,
  UserCircle,
  ArrowLeftRight
} from 'lucide-react';
import { useUIStore } from '@/shared/stores/ui.store';
import { useAuthActions, useUser } from '@/shared/hooks/useAuth';
import { Dropdown } from '@/shared/components/ui/Dropdown';
import { ROUTES } from '@/routes/paths';
import { BackButton } from './BackButton';
import { NotificationDropdown } from '@/shared/components/Notification/NotificationDropdown';
import { SystemHealthModal } from '@/shared/components/SystemHealth/SystemHealthModal';
import { CommandPalette } from '@/shared/components/CommandPalette/CommandPalette';
import { HelpModal } from '@/shared/components/Help/HelpModal';
import { CurrencyExchangerModal } from '@/shared/components/currency';
import { CrudeOilTopbarPill } from '@/shared/components/CrudeOilBanner/CrudeOilBanner';
import { AttendanceWidget } from '@/features/attendance/components/AttendanceWidget';
import { CheckInPrompt } from '@/features/attendance/components/CheckInPrompt';
import { playNotificationSound, playSuccessSound } from '@/shared/utils/sound';

export function Topbar() {
  const toggleCollapsed = useUIStore((s) => s.toggleSidebarCollapsed);
  const user = useUser();
  const { logout } = useAuthActions();
  const navigate = useNavigate();

  const [searchQuery, ] = useState('');
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showHealthModal, setShowHealthModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showForexModal, setShowForexModal] = useState(false);

  // A Field Executive's top bar carries only what their field work needs
  // (2026-10-07): no company-wide search, no Forex desk, no full Copilot page.
  const isFieldExecutive = String(user?.role || '') === 'field_executive';

  // Global keyboard shortcut listener for Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isFieldExecutive) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setShowCommandPalette((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFieldExecutive]);

  const onLogout = async () => {
    playSuccessSound();
    await logout();
    navigate(ROUTES.login, { replace: true });
  };

  const handleOpenCopilot = () => {
    playSuccessSound();
    navigate(ROUTES.aiCopilot);
  };

  const handleSearchClick = () => {
    setShowCommandPalette(true);
  };

  // What this user's workspace is, next to the logo. It said "EXECUTIVE
  // COMMAND" to every BDM, dispatcher and accountant alike.
  const role = String(user?.role || '').toLowerCase();
  const department = String(user?.department || '').toLowerCase();
  const isAdminUser = ['superadmin', 'admin', 'ceo'].includes(role);
  const workspaceLabel = role === 'superadmin' || role === 'ceo'
    ? 'Admin Console'
    : ({
        sales: 'Sales Workspace',
        accounts: 'Accounts Workspace',
        dispatch: 'Dispatch Workspace',
        operations: 'Operations Workspace',
        production: 'Production Workspace',
        hr: 'HR Workspace',
        management: 'Management',
      } as Record<string, string>)[department] || 'Workspace';

  return (
    <>
      <header
        className="sticky top-0 z-40 flex h-14 items-center justify-between gap-4 border-b border-border bg-surface text-on-surface px-5 shadow-xs"
        data-shell-plane="top-navigation"
      >
        {/* Left: Sidebar Collapse Icon, ZYNX Brand & Executive Command Subtitle */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggleCollapsed}
            className="flex items-center justify-center rounded-lg border border-input p-1.5 text-on-surface-variant transition-colors hover:bg-muted hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Toggle sidebar"
          >
            {/* h-4.5/w-4.5 are not on the spacing scale, so the icon ignored the
                class and rendered at lucide's 24px default — the one control in
                the bar that was a different size than it was written to be. */}
            <PanelLeftClose className="h-4 w-4" />
          </button>

          <BackButton />

          {/* RBM Lubrifiants Logo */}
          <div
            onClick={() => navigate(ROUTES.dashboard)}
            className="flex items-center gap-2.5 cursor-pointer select-none group"
            title={`RBM Oil Corporation – ${workspaceLabel}`}
          >
            <img
              src="/rbm-logo.png"
              alt="RBM Lubrifiants"
              className="h-[38px] w-[38px] rounded-full object-contain shrink-0 transition-transform group-hover:scale-105 drop-shadow-sm"
              onError={(e) => {
                // fallback to base64 embedded logo if file not found
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <span className="select-none hidden sm:inline text-on-surface-subtle">|</span>
            <span className="select-none hidden sm:inline text-label-caps uppercase text-on-surface-subtle transition-colors group-hover:text-on-surface">
              {workspaceLabel}
            </span>
          </div>
        </div>

        {/* Center: Search Bar with Pill Shape */}
        <div className={isFieldExecutive ? 'flex-1' : 'flex-1 max-w-xl mx-4 hidden md:block'}>
          {!isFieldExecutive && (
          <div
            onClick={handleSearchClick}
            className="relative flex items-center cursor-pointer group"
          >
            <Search className="pointer-events-none absolute left-3.5 h-4 w-4 text-on-surface-subtle transition-colors group-hover:text-primary" />
            <input
              type="text"
              readOnly
              value={searchQuery}
              onClick={handleSearchClick}
              placeholder="Search people, clients, deals, projects..."
              aria-label="Search the workspace"
              className="w-full cursor-pointer rounded-full border border-input bg-surface-variant/70 py-1.5 pl-10 pr-12 text-body-sm text-on-surface shadow-xs transition placeholder:text-on-surface-subtle group-hover:bg-surface group-hover:border-primary/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <span className="pointer-events-none absolute right-3 select-none rounded-md border border-border bg-surface px-1.5 py-0.5 text-[10px] font-bold text-on-surface-subtle shadow-2xs">
              ⌘ K
            </span>
          </div>
          )}
        </div>

        {/* Right Controls: Systems Operational, Notification, AI Copilot, Help & Profile */}
        <div className="flex items-center gap-2.5 sm:gap-3">
          {/* System health — an admin concern; a BDM cannot act on it. */}
          {isAdminUser && <button
            type="button"
            onClick={() => setShowHealthModal(true)}
            title="Click to view live system health status"
            className="hidden xl:flex cursor-pointer items-center gap-1.5 rounded-full border border-success/20 bg-success/10 px-3 py-1 text-body-sm font-semibold text-success-text shadow-2xs transition-colors hover:bg-success/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="h-2 w-2 rounded-full bg-success animate-pulse" aria-hidden />
            <span>Systems Operational</span>
          </button>}

          {/* Attendance: Check in / On duty · 3h 12m / Check out — on every
              page for employees (not the owner). */}
          {user && String(user.role || '').toLowerCase() !== 'superadmin' && <AttendanceWidget />}

          {/* Notification Bell with Audio Chime and Rich Flyout */}
          <NotificationDropdown />

          {/* Crude Oil & Petroleum Market Price Pill */}
          {!isFieldExecutive && <CrudeOilTopbarPill />}

          {/* Currency Exchanger / Forex Desk Button */}
          {!isFieldExecutive && <button
            type="button"
            onClick={() => setShowForexModal(true)}
            title="Open Currency Exchanger & Live Forex Desk (Frankfurter API)"
            className="hidden sm:flex cursor-pointer items-center gap-1.5 rounded-full border border-primary/20 bg-primary-container px-3 py-1.5 text-body-sm font-semibold text-on-primary-container shadow-2xs transition-colors hover:bg-primary-container/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ArrowLeftRight className="h-3.5 w-3.5 text-primary" aria-hidden />
            <span>Forex</span>
          </button>}

          {/* AI Copilot Pill Button */}
          {!isFieldExecutive && <button
            type="button"
            onClick={handleOpenCopilot}
            title="Open ZYNX AI Copilot"
            className="hidden sm:flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary-container px-3.5 py-1.5 text-body-sm font-semibold text-on-primary-container shadow-2xs transition-colors hover:bg-primary-container/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden />
            <span>AI Copilot</span>
          </button>}

          {/* Help Circle Button */}
          <button
            type="button"
            onClick={() => setShowHelpModal(true)}
            className="rounded-full p-1.5 text-on-surface-subtle transition-colors hover:bg-muted hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Help and Shortcuts"
            title="Help and Keyboard Shortcuts"
          >
            <HelpCircle className="h-4 w-4" aria-hidden />
          </button>

          {/* User Profile Dropdown */}
          {user && (
            <Dropdown
              align="right"
              trigger={
                <span className="flex items-center gap-2 rounded-full py-1 px-1.5 transition-colors select-none hover:bg-muted">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full border border-primary/20 bg-primary-container text-body-sm font-bold text-on-primary-container">
                    {user.role === 'superadmin' || user.role === 'admin'
                      ? 'SA'
                      : (user.displayName || user.username || 'U').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="hidden sm:flex items-center gap-1 text-data-sm font-semibold text-on-surface">
                    <span>
                      {user.role === 'superadmin'
                        ? 'Super Admin'
                        : user.displayName || user.username}
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 text-on-surface-subtle" aria-hidden />
                  </div>
                </span>
              }
              items={[
                {
                  label: 'My Profile',
                  icon: <UserCircle className="h-4 w-4 text-on-surface-subtle" aria-hidden />,
                  onClick: () => navigate(ROUTES.myProfile),
                },
                {
                  label: 'My Settings & Profile',
                  icon: <Settings className="h-4 w-4 text-on-surface-subtle" aria-hidden />,
                  onClick: () => navigate(ROUTES.settings),
                },
                {
                  label: 'Team & Organization',
                  icon: <Users className="h-4 w-4 text-on-surface-subtle" aria-hidden />,
                  onClick: () => navigate(ROUTES.hr),
                },
                {
                  label: 'Test Notification Sound',
                  icon: <Volume2 className="h-4 w-4 text-success-text" aria-hidden />,
                  onClick: () => playNotificationSound(true),
                },
                {
                  label: 'Log out',
                  icon: <LogOut className="h-4 w-4" aria-hidden />,
                  onClick: onLogout,
                  danger: true,
                },
              ]}
            />
          )}
        </div>
      </header>

      {/* Global Interactive Modals */}
      <CommandPalette
        open={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        initialQuery={searchQuery}
      />
      <SystemHealthModal
        open={showHealthModal}
        onClose={() => setShowHealthModal(false)}
      />
      <HelpModal
        open={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />
      {/* After login: asks an employee who has not checked in today. */}
      {user && <CheckInPrompt />}

      <CurrencyExchangerModal
        open={showForexModal}
        onClose={() => setShowForexModal(false)}
      />
    </>
  );
}
